import { Modal } from "react-bootstrap";
import { useMemo } from "react";
import timeService from "../../../services/time/timeService";
import QuickForm from "../../Forms/QuickForm/QuickForm";

export default function SelectCleaningModal(props: {
    active: boolean,
    onClose: () => void,
    onSelect: (cleaningData: { 
        cleaningType: 'full' | 'basic', 
        serviceDate: Date 
    }) => void,
    bookingCheckIn: Date | string,
    bookingCheckOut: Date | string,
    existingCleaningDates: Date[],
    message?: string,
    title?: string
}) {
    const {
        active,
        onClose,
        onSelect,
        bookingCheckIn,
        bookingCheckOut,
        existingCleaningDates = [],
        message = "Select cleaning type and date",
        title = "Add Additional Cleaning"
    } = props;

    const handleSubmit = async (values: any) => {
        if (!values.serviceDate) return;
        
        // Parse the selected date and set time to 2:00 PM (14:00)

        const [year, month, day] = values.serviceDate.split('-').map((part: string) => parseInt(part, 10));
        const serviceDateTime = timeService.createLuxonDateFromObject({
            year,
            month,
            day,
            hour: 14,
            minute: 0,
            second: 0,
            millisecond: 0
        })
        
        onSelect({
            cleaningType: values.cleaningType,
            serviceDate: serviceDateTime.toJSDate()
        });
        
        onClose();
    };

    // Generate available dates between check-in and check-out
    const availableDates = useMemo(() => {
        const dates: { value: string, text: string }[] = [];
        
        if (!bookingCheckIn || !bookingCheckOut) return dates;
        
        const checkIn = timeService.createLuxonDate(
            typeof bookingCheckIn === 'string' ? new Date(bookingCheckIn) : bookingCheckIn
        ).startOf('day');
        const checkOut = timeService.createLuxonDate(
            typeof bookingCheckOut === 'string' ? new Date(bookingCheckOut) : bookingCheckOut
        ).startOf('day');
        const today = timeService.getCurrentTime().startOf('day');
        
        // Convert existing cleaning dates to ISO date strings for comparison
        const existingDateStrings = existingCleaningDates.map(date => 
            timeService.getFormattedTime(date, 'yyyy-MM-dd')
        );
        
        let currentDate = checkIn;
        
        
        while (currentDate.toMillis() < checkOut.toMillis()) {
            const dateStr = timeService.getFormattedTime(currentDate.toJSDate(), 'yyyy-MM-dd');
            const isPast = currentDate.toMillis() < today.toMillis();
            const hasExistingCleaning = existingDateStrings.includes(dateStr);
            
            if (!isPast && !hasExistingCleaning && dateStr) {
                dates.push({
                    value: dateStr,
                    text: currentDate.toFormat('MMMM dd, yyyy')
                });
            }
            
            currentDate = currentDate.plus({ days: 1 });
        }
        
        return dates;
    }, [bookingCheckIn, bookingCheckOut, existingCleaningDates]);

    return (
        <Modal
            show={active}
            onHide={onClose}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    {title}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-4">{message}</p>

                {availableDates.length === 0 && (
                    <p className="text-muted">No available dates for additional cleaning.</p>
                )}

                {availableDates.length > 0 && (
                    <QuickForm
                        initialData={{
                            cleaningType: 'full',
                            serviceDate: '',
                        }}
                        onSubmit={handleSubmit}
                        fields={[
                            {
                                fieldName: 'cleaningType',
                                fieldLabel: 'Cleaning Type',
                                fieldType: 'select',
                                placeholder: '- Select Cleaning Type -',
                                required: true,
                                options: [
                                    { value: 'full', text: 'Full Cleaning' },
                                    { value: 'basic', text: 'Basic Cleaning' },
                                ],
                            },
                            {
                                fieldName: 'serviceDate',
                                fieldLabel: 'Service Date',
                                fieldType: 'select',
                                placeholder: '- Select Date -',
                                required: true,
                                options: availableDates,
                            },
                        ]}
                        submitText="Add Cleaning"
                        onCancel={onClose}
                        cancelText="Cancel"
                    />
                )}
            </Modal.Body>
        </Modal>
    );
}
